import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, User } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import RichTextEditor from "@/components/RichTextEditor";
import { useAuth } from "@/context/AuthContext";

const DESIGNATIONS = ["President", "General Secretary", "Principal"];

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const emptyForm = {
  designation: "",
  name: "",
  photo_url: "",
  photo_public_id: "",
  message_html: "",
};

export default function LeadershipMessages() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [messagesByDesig, setMessagesByDesig] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institute/leadership");
      const map = {};
      data.forEach((m) => {
        map[m.designation] = m;
      });
      setMessagesByDesig(map);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openFor = (desig) => {
    const existing = messagesByDesig[desig];
    setForm({
      designation: desig,
      name: existing?.name || "",
      photo_url: existing?.photo_url || "",
      photo_public_id: existing?.photo_public_id || "",
      message_html: existing?.message_html || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.designation) {
      toast.error("Designation is required");
      return;
    }
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!(form.message_html || "").trim()) {
      toast.error("Message is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(
        `/institute/leadership/${encodeURIComponent(form.designation)}`,
        form,
      );
      setMessagesByDesig((m) => ({ ...m, [data.designation]: data }));
      toast.success("Message saved");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const desig = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/institute/leadership/${encodeURIComponent(desig)}`);
      setMessagesByDesig((m) => {
        const next = { ...m };
        delete next[desig];
        return next;
      });
      toast.success("Message removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto" data-testid="leadership-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Leadership</div>
        <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Leadership Messages</h1>
        <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
          One message per designation. The most recent edit replaces the previous content. Photos
          should be roughly square (400×400 px) — JPG / PNG / WebP, up to 200 KB.
        </p>
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="leadership-grid">
          {DESIGNATIONS.map((desig) => {
            const item = messagesByDesig[desig];
            return (
              <div
                key={desig}
                data-testid={`leadership-card-${desig.replace(/\s+/g, "-")}`}
                className="bg-white border border-[#e5e0d8] rounded-sm p-6 flex flex-col"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 rounded-sm bg-[#f9f8f6] border border-[#e5e0d8] overflow-hidden shrink-0 flex items-center justify-center text-[#234333]">
                    {item?.photo_url ? (
                      <img
                        src={item.photo_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User size={24} strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                      {desig}
                    </div>
                    <div className="mt-1 font-serif text-xl text-[#1c1a17] leading-tight truncate">
                      {item?.name || (
                        <span className="text-[#6b655b] italic">Not added yet</span>
                      )}
                    </div>
                  </div>
                </div>

                {item?.message_html ? (
                  <div
                    data-testid={`leadership-preview-${desig.replace(/\s+/g, "-")}`}
                    className="prose prose-sm max-w-none text-[#1c1a17] text-sm leading-relaxed flex-1 line-clamp-6 mb-3"
                    dangerouslySetInnerHTML={{ __html: item.message_html }}
                  />
                ) : (
                  <div className="text-sm text-[#6b655b] italic flex-1 mb-3">
                    No message added.
                  </div>
                )}

                {canEdit && (
                  <div className="flex gap-1 pt-2 border-t border-[#e5e0d8] mt-auto">
                    <button
                      onClick={() => openFor(desig)}
                      data-testid={`leadership-edit-${desig.replace(/\s+/g, "-")}`}
                      className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={12} strokeWidth={1.5} /> {item ? "Edit" : "Add"}
                    </button>
                    {item && (
                      <button
                        onClick={() => setConfirmDelete(desig)}
                        data-testid={`leadership-delete-${desig.replace(/\s+/g, "-")}`}
                        className="inline-flex items-center gap-1 text-xs text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                      >
                        <Trash2 size={12} strokeWidth={1.5} /> Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto"
          data-testid="leadership-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {messagesByDesig[form.designation] ? "Edit" : "Add"} Message ·{" "}
              {form.designation}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6b655b]">
              Enter the person's name, upload a square photo, and write a rich-text message.
              One message per designation — saving replaces the previous content.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Designation *
              </div>
              <select
                disabled
                data-testid="leadership-designation-select"
                className={inputCls + " bg-[#f9f8f6]"}
                value={form.designation}
                onChange={(e) =>
                  setForm((f) => ({ ...f, designation: e.target.value }))
                }
              >
                {DESIGNATIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <div className="mt-1 text-[11px] text-[#6b655b]">
                Designation is fixed for this card — there can be only one message per
                designation.
              </div>
            </label>

            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Person's Name *
              </div>
              <input
                data-testid="leadership-name-input"
                className={inputCls}
                placeholder="e.g., Dr. R. K. Sharma"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </label>

            <FileUploader
              kind="inst_leader_photo"
              label="Photo (400×400 square)"
              url={form.photo_url}
              testid="leadership-photo"
              onChange={({ url, public_id }) =>
                setForm((f) => ({ ...f, photo_url: url, photo_public_id: public_id }))
              }
            />

            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Message *
              </div>
              <RichTextEditor
                testid="leadership-message-editor"
                value={form.message_html}
                onChange={(html) => setForm((f) => ({ ...f, message_html: html }))}
                placeholder="Write a warm message — vision, achievements, and what you wish to convey to students and visitors."
              />
              <div className="mt-1 text-[11px] text-[#6b655b]">
                Script tags and unsafe HTML are stripped server-side. Links open in a new tab.
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="leadership-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="leadership-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Delete this message?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              The "{confirmDelete}" message and its photo will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="leadership-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
